import { type CalendarDate, getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { DatePicker } from '@customerio/pluma-components/react';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { isEmptyString } from '@/utils/string';
import type { ActivityLogFilters } from '../types';

/** The two date-range fields of the feed's filters. */
type DateRange = Pick<ActivityLogFilters, 'from' | 'to'>;

/** Activity time window is limited to the last 30 days (inclusive of today). */
const DATE_RANGE_DAYS = 30;

interface DateRangeFilterProps {
  /** RFC3339 strings (or '') — the `from`/`to` query params forwarded to the logs endpoint. */
  from: string;
  to: string;
  onChange: (range: DateRange) => void;
}

/** `2026-06-30T…Z` → the `CalendarDate` the range picker wants; `''`/invalid → undefined. */
export function rfcToCalendarDate(value: string): CalendarDate | undefined {
  if (isEmptyString(value)) return undefined;
  try {
    return parseDate(value.slice(0, 10));
  } catch {
    return undefined;
  }
}

/** A `{ start, end }` calendar range → inclusive RFC3339 day bounds (start-of-day / end-of-day, UTC). */
export function rangeToFilters(range: { start: CalendarDate; end: CalendarDate }): DateRange {
  return { from: `${range.start.toString()}T00:00:00Z`, to: `${range.end.toString()}T23:59:59Z` };
}

/**
 * Date-range filter for the activity time window. Gated behind the `activity-logs-time-range`
 * flag (renders nothing when the flag is off). Uses Pluma's range `DatePicker` (a single control)
 * with a 30-day `minDate` window.
 */
export function DateRangeFilter({ from, to, onChange }: DateRangeFilterProps) {
  const isEnabled = useFeatureFlag(AccountFeatureFlag.ActivityLogsTimeRange);
  if (!isEnabled) return null;

  const todayDate = today(getLocalTimeZone());
  const minDate = todayDate.subtract({ days: DATE_RANGE_DAYS - 1 });

  const start = rfcToCalendarDate(from);
  const end = rfcToCalendarDate(to);
  const value = start != null && end != null ? { start, end } : undefined;

  return (
    <DatePicker
      label="Date range"
      isRange
      withPresets
      value={value}
      minDate={minDate}
      maxDate={todayDate}
      // Range mode passes a `{ start, end }` value; `'start' in next` narrows it (single-date /
      // zoned values, which this picker doesn't produce, and null → clear the filter).
      onChange={(next) =>
        onChange(next != null && 'start' in next && 'end' in next ? rangeToFilters(next) : { from: '', to: '' })
      }
      size="sm"
    />
  );
}
